import React, { useState, useEffect } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Plus, Trash2, Edit2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LibraryTable = ({ libraryDocs, language, onDelete, onRefresh }) => (
  <div className="bg-white border border-stone-200 overflow-hidden">
    <div className="p-4 border-b border-stone-200 flex items-center justify-between">
      <h3 className="text-stone-900 font-semibold">{language === 'sv' ? 'Biblioteksdokument' : 'Library Documents'}</h3>
      <LibraryFormDialog onSuccess={onRefresh} language={language} />
    </div>
    <div className="overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow className="bg-stone-50">
            <TableHead>{language === 'sv' ? 'Titel' : 'Title'}</TableHead>
            <TableHead>{language === 'sv' ? 'Kategori' : 'Category'}</TableHead>
            <TableHead>{language === 'sv' ? 'Filtyp' : 'File Type'}</TableHead>
            <TableHead>{language === 'sv' ? 'Version' : 'Version'}</TableHead>
            <TableHead>{language === 'sv' ? 'Storlek' : 'Size'}</TableHead>
            <TableHead className="text-right">{language === 'sv' ? 'Åtgärder' : 'Actions'}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {libraryDocs.length === 0 ? (
            <TableRow>
              <TableCell colSpan={6} className="text-center text-stone-500 py-8">
                {language === 'sv' ? 'Inga dokument' : 'No documents'}
              </TableCell>
            </TableRow>
          ) : (
            libraryDocs.map((doc) => (
              <TableRow key={doc.id} className="hover:bg-stone-50">
                <TableCell className="font-medium">
                  {language === 'sv' ? doc.title_sv : doc.title_en}
                </TableCell>
                <TableCell>{doc.category}</TableCell>
                <TableCell>
                  <span className="px-2 py-1 bg-stone-100 text-stone-700 text-xs rounded">
                    {doc.file_type}
                  </span>
                </TableCell>
                <TableCell className="text-sm text-stone-600">{doc.version || '-'}</TableCell>
                <TableCell className="text-sm text-stone-600">{doc.file_size || '-'}</TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-1">
                    <LibraryFormDialog onSuccess={onRefresh} language={language} editData={doc} />
                    <Button 
                      variant="ghost" 
                      size="icon" 
                      onClick={() => onDelete(doc.id)}
                      className="text-stone-500 hover:text-red-600"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </div>
  </div>
);

const LibraryFormDialog = ({ onSuccess, language, editData = null }) => {
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [titleSv, setTitleSv] = useState('');
  const [titleEn, setTitleEn] = useState('');
  const [descSv, setDescSv] = useState('');
  const [descEn, setDescEn] = useState('');
  const [category, setCategory] = useState('Produktkataloger');
  const [fileUrl, setFileUrl] = useState('');
  const [fileType, setFileType] = useState('PDF');
  const [fileSize, setFileSize] = useState('');
  const [version, setVersion] = useState('');
  const [docLanguage, setDocLanguage] = useState('');
  const [compatibility, setCompatibility] = useState('');
  const [order, setOrder] = useState(0);

  useEffect(() => {
    if (editData && open) {
      setTitleSv(editData.title_sv || '');
      setTitleEn(editData.title_en || '');
      setDescSv(editData.description_sv || '');
      setDescEn(editData.description_en || '');
      setCategory(editData.category || 'Produktkataloger');
      setFileUrl(editData.file_url || '');
      setFileType(editData.file_type || 'PDF');
      setFileSize(editData.file_size || '');
      setVersion(editData.version || '');
      setDocLanguage(editData.language || '');
      setCompatibility(editData.compatibility || '');
      setOrder(editData.order || 0);
    } else if (open) {
      setTitleSv(''); setTitleEn(''); setDescSv(''); setDescEn(''); setCategory('Produktkataloger');
      setFileUrl(''); setFileType('PDF'); setFileSize(''); setVersion(''); setDocLanguage(''); setCompatibility(''); setOrder(0);
    }
  }, [editData, open]);

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(`${API}/library/upload`, formData);
      setFileUrl(res.data.url);
      
      // Auto-detect file type
      const ext = file.name.split('.').pop().toUpperCase();
      setFileType(ext);
      
      // Auto-calculate file size
      const sizeMB = (file.size / (1024 * 1024)).toFixed(2);
      setFileSize(`${sizeMB} MB`);
      
      toast.success(language === 'sv' ? 'Fil uppladdad' : 'File uploaded');
    } catch (error) {
      toast.error(language === 'sv' ? 'Uppladdning misslyckades' : 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const data = {
        title_sv: titleSv,
        title_en: titleEn,
        description_sv: descSv,
        description_en: descEn,
        category,
        file_url: fileUrl,
        file_type: fileType,
        file_size: fileSize,
        version,
        language: docLanguage,
        compatibility,
        order: parseInt(order) || 0
      };
      
      if (editData) {
        await axios.put(`${API}/library/${editData.id}`, data);
        toast.success(language === 'sv' ? 'Dokument uppdaterat' : 'Document updated');
      } else {
        await axios.post(`${API}/library`, data);
        toast.success(language === 'sv' ? 'Dokument skapat' : 'Document created');
      }
      
      setOpen(false);
      onSuccess();
    } catch (error) {
      toast.error('Error');
    } finally {
      setSubmitting(false);
    }
  };

  const predefinedCategories = [
    'Produktkataloger',
    'Tekniska manualer',
    'Mjukvara',
    'Certifikat',
    'Installationsguider',
    'Datablad',
    'Ritningar'
  ];

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {editData ? (
          <Button variant="ghost" size="icon" className="text-stone-500 hover:text-stone-700">
            <Edit2 className="w-4 h-4" />
          </Button>
        ) : (
          <Button className="bg-stone-900 text-white hover:bg-stone-800">
            <Plus className="w-4 h-4 mr-2" /> {language === 'sv' ? 'Nytt dokument' : 'New document'}
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="bg-white border-stone-200 max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {editData 
              ? (language === 'sv' ? 'Redigera dokument' : 'Edit document')
              : (language === 'sv' ? 'Lägg till dokument' : 'Add document')
            }
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* File Upload */}
          <div>
            <Label>{language === 'sv' ? 'Fil' : 'File'} *</Label>
            <Input 
              type="file" 
              onChange={handleFileUpload}
              className="bg-white border-stone-300"
              disabled={uploading}
            />
            {uploading && <p className="text-sm text-stone-500 mt-1">{language === 'sv' ? 'Laddar upp...' : 'Uploading...'}</p>}
            {fileUrl && <p className="text-sm text-emerald-600 mt-1">✓ {language === 'sv' ? 'Fil uppladdad' : 'File uploaded'}</p>}
          </div>

          {/* Title */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>{language === 'sv' ? 'Titel (SV)' : 'Title (SV)'} *</Label>
              <Input 
                value={titleSv}
                onChange={(e) => setTitleSv(e.target.value)}
                required
                className="bg-white border-stone-300"
              />
            </div>
            <div>
              <Label>{language === 'sv' ? 'Titel (EN)' : 'Title (EN)'} *</Label>
              <Input 
                value={titleEn}
                onChange={(e) => setTitleEn(e.target.value)}
                required
                className="bg-white border-stone-300"
              />
            </div>
          </div>

          {/* Description */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>{language === 'sv' ? 'Beskrivning (SV)' : 'Description (SV)'}</Label>
              <Textarea 
                value={descSv}
                onChange={(e) => setDescSv(e.target.value)}
                rows={3}
                className="bg-white border-stone-300"
              />
            </div>
            <div>
              <Label>{language === 'sv' ? 'Beskrivning (EN)' : 'Description (EN)'}</Label>
              <Textarea 
                value={descEn}
                onChange={(e) => setDescEn(e.target.value)}
                rows={3}
                className="bg-white border-stone-300"
              />
            </div>
          </div>

          {/* Category and File Type */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>{language === 'sv' ? 'Kategori' : 'Category'} *</Label>
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger className="bg-white border-stone-300">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-white">
                  {predefinedCategories.map(cat => (
                    <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>{language === 'sv' ? 'Filtyp' : 'File Type'}</Label>
              <Input 
                value={fileType}
                onChange={(e) => setFileType(e.target.value.toUpperCase())}
                placeholder="PDF, ZIP, EXE..."
                className="bg-white border-stone-300"
              />
            </div>
          </div>

          {/* Metadata */}
          <div className="grid grid-cols-3 gap-4">
            <div>
              <Label>{language === 'sv' ? 'Version' : 'Version'}</Label>
              <Input 
                value={version}
                onChange={(e) => setVersion(e.target.value)}
                placeholder="1.0.2"
                className="bg-white border-stone-300"
              />
            </div>
            <div>
              <Label>{language === 'sv' ? 'Språk' : 'Language'}</Label>
              <Input 
                value={docLanguage}
                onChange={(e) => setDocLanguage(e.target.value)}
                placeholder="SV, EN, SV/EN"
                className="bg-white border-stone-300"
              />
            </div>
            <div>
              <Label>{language === 'sv' ? 'Filstorlek' : 'File Size'}</Label>
              <Input 
                value={fileSize}
                onChange={(e) => setFileSize(e.target.value)}
                placeholder="2.5 MB"
                className="bg-white border-stone-300"
              />
            </div>
          </div>

          {/* Compatibility and Order */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>{language === 'sv' ? 'Kompatibilitet' : 'Compatibility'}</Label>
              <Input 
                value={compatibility}
                onChange={(e) => setCompatibility(e.target.value)}
                placeholder="Stratos Pico, GE552..."
                className="bg-white border-stone-300"
              />
            </div>
            <div>
              <Label>{language === 'sv' ? 'Ordning' : 'Order'}</Label>
              <Input 
                type="number"
                value={order}
                onChange={(e) => setOrder(parseInt(e.target.value) || 0)}
                className="bg-white border-stone-300"
              />
            </div>
          </div>

          <DialogFooter>
            <Button 
              type="submit" 
              disabled={submitting || !fileUrl}
              className="bg-stone-900 text-white hover:bg-stone-800 rounded-none"
            >
              {submitting ? '...' : (language === 'sv' ? 'Spara' : 'Save')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default LibraryTable;
export { LibraryFormDialog };
